import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CreateProjectFromDrawingDialog } from '../../components/drawings/CreateProjectFromDrawingDialog';
import { DrawingHeader } from '../../components/drawings/DrawingHeader';
import { DrawingTarget, UpdateProjectDialog } from '../../components/drawings/UpdateProjectDialog';
import { drawingsApi, projectGeometryApi } from '../../components/drawings/drawingsApi';
import { renderPlanThumbnail } from '../../components/drawings/planThumbnail';
import { useDrawingAutosave } from '../../components/drawings/useDrawingAutosave';
import CadProjectWizard, { EditedPlan } from '../../components/new-project-cad/CadProjectWizard';
import { gzipDxf } from '../../components/new-project-cad/cad-import/cadImportClient';
import {
  DrawingDocument,
  LoadedDrawing,
  fromDrawingDocument,
  toDrawingDocument,
} from '../../components/new-project-cad/drawingDocument';
import { diffDrawings, hasChanges } from '../../components/new-project-cad/planEditing';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useDrawingsEnabled } from '../../hooks/useDrawingsEnabled';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { userReducer } from '../../store/reducers/user.reducer';
import { getNorms } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { Drawing, ProjectGeometry } from '../../types/drawing';

const PREVIEW_INTERVAL_MS = 20_000;

const samePlan = (a: EditedPlan, b: EditedPlan) =>
  a.polygons === b.polygons &&
  a.metersPerUnit === b.metersPerUnit &&
  a.scaleRatio === b.scaleRatio &&
  a.scaleSet === b.scaleSet &&
  a.imageScale === b.imageScale &&
  a.cad === b.cad &&
  a.reference === b.reference;

/** A drawing in the polygon editor (Drawings tab); `?project=` when opened from a project. */
export function DrawingEditorPage() {
  const { t } = useTranslation();
  const enabled = useDrawingsEnabled();
  const navigate = useNavigate();
  const idParam = useParams().id;
  // "New drawing" opens /drawings/new: nothing is stored until the drawing gets something in it.
  const isNew = idParam === 'new';
  const drawingId = isNew ? null : Number(idParam);
  const [searchParams] = useSearchParams();
  const projectId = Number(searchParams.get('project')) || null;
  const newName = searchParams.get('name');
  // From the Drawings page: `?start=draw` (blank canvas) or a plan file picked there.
  const importFile = (useLocation().state as { importFile?: File } | null)?.importFile;
  const [startWith] = useState(() =>
    !isNew
      ? null
      : importFile instanceof File
        ? { file: importFile }
        : searchParams.get('start') === 'draw'
          ? ({ draw: true } as const)
          : null,
  );
  // The drawing this page has just created from a new one: already on screen, not loaded again.
  const created = useRef<number | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [drawing, setDrawing] = useState<Drawing | 'loading' | 'failed'>('loading');
  // Opened from a project: the drawing as that project was last built from it.
  const [projectDocument, setProjectDocument] = useState<ProjectGeometry['drawing']>(null);

  useEffect(() => {
    if (drawingId === null) {
      setDrawing({
        id: 0,
        name: newName?.trim() || t('drawings.untitled'),
        document: null,
        lockKey: 0,
        updatedAt: new Date().toISOString(),
        previewImageUrl: null,
        referenceImageUrl: null,
        sourceUrl: null,
        revisions: [],
      });
      return;
    }
    if (created.current === drawingId) return;
    let cancelled = false;
    setDrawing('loading');
    Promise.all([drawingsApi.get(drawingId), projectId ? projectGeometryApi.get(projectId).catch(() => null) : null])
      .then(([loaded, geometry]) => {
        if (cancelled) return;
        setProjectDocument(geometry?.drawing ?? null);
        setDrawing(loaded);
      })
      .catch(() => !cancelled && setDrawing('failed'));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingId, projectId, loadKey]);

  if (!enabled) return <Navigate to="/projects" replace />;
  if (typeof drawing === 'string') {
    return (
      <div className="w-full h-[calc(100dvh-64px)] flex items-center justify-center bg-[#0d0d0d] text-sm text-gray-300">
        {drawing === 'loading' ? t('common.loading') : t('drawings.loadFailed')}
      </div>
    );
  }
  return (
    <DrawingEditor
      key={loadKey}
      drawing={drawing}
      isNew={isNew}
      startWith={startWith}
      projectDocument={projectDocument}
      onReload={() => setLoadKey((key) => key + 1)}
      onCreated={(id) => {
        created.current = id;
        navigate(`/drawings/${id}`, { replace: true });
      }}
    />
  );
}

interface DrawingEditorProps {
  drawing: Drawing;
  /** Not stored yet: created on the first save, when it has something in it. */
  isNew: boolean;
  startWith: { draw: true } | { file: File } | null;
  projectDocument: ProjectGeometry['drawing'];
  onReload: () => void;
  onCreated: (id: number) => void;
}

/** Worth storing: a polygon, an imported drawing or a plan image. */
const hasContent = (plan: EditedPlan) => plan.polygons.length > 0 || !!plan.cad || plan.imageScale !== null;

function DrawingEditor({ drawing, isNew, startWith, projectDocument, onReload, onCreated }: DrawingEditorProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const norms = useAppSelector(getNorms);
  const projectId = Number(useSearchParams()[0].get('project')) || null;
  // From a project the changes stay here until the project is updated; then the user picks whether
  // they go to this drawing or to a new one.
  const fromProject = projectId !== null;
  const [name, setName] = useState(drawing.name);
  const nameRef = useRef(drawing.name);
  // The drawing's id once it is stored (a new one gets it on its first save).
  const idRef = useRef<number | null>(isNew ? null : drawing.id);
  const [storedId, setStoredId] = useState(idRef.current);
  const [revisions, setRevisions] = useState(drawing.revisions);
  const [referenceImageUrl, setReferenceImageUrl] = useState(drawing.referenceImageUrl);
  // A plan image imported from a project (or into a drawing not stored yet), saved with the change.
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  // The same for an imported DWG/DXF (gzipped DXF).
  const [pendingSource, setPendingSource] = useState<Blob | null>(null);
  const pending = useRef<{ image: File | null; source: Blob | null }>({ image: null, source: null });
  pending.current = { image: pendingImage, source: pendingSource };
  const [dialog, setDialog] = useState<{ kind: 'create' | 'update'; plan: EditedPlan } | null>(null);
  const drawingPlan = useMemo(() => fromDrawingDocument(drawing.document), [drawing.document]);
  const projectPlan = useMemo(() => fromDrawingDocument(projectDocument), [projectDocument]);
  // Another project may have saved its change to the drawing since this one was built from it. The
  // editor then starts on this project's version, so it does not take that change unnoticed.
  const movedOn = useMemo(
    () => !!projectPlan && !!drawingPlan && hasChanges(diffDrawings(projectPlan.polygons, drawingPlan.polygons)),
    [projectPlan, drawingPlan],
  );
  const [base, setBase] = useState<'project' | 'drawing'>(movedOn ? 'project' : 'drawing');
  // The plan the editor opens on; a restored checkpoint reopens it on that plan.
  const [opened, setOpened] = useState<{ plan: LoadedDrawing | null; key: number }>({
    plan: movedOn ? projectPlan : drawingPlan,
    key: 0,
  });
  const live = useRef<EditedPlan | null>(null);
  const lastPreview = useRef(0);
  const origin = useMemo<[number, number]>(() => opened.plan?.origin ?? [0, 0], [opened.plan]);

  // Norm names for keeping a project's lighting when its geometry is updated.
  useEffect(() => {
    if (norms.length === 0) dispatch(userReducer.actions.loadNorms());
  }, [dispatch, norms.length]);

  const build = useCallback(
    () =>
      live.current &&
      toDrawingDocument(live.current, { imageScale: live.current.imageScale, origin, cad: live.current.cad }),
    [origin],
  );
  // A new drawing is created with what it has so far; its image and file follow.
  const create = useCallback(
    async (doc: DrawingDocument) => {
      const createdDrawing = await drawingsApi.create(nameRef.current, doc);
      idRef.current = createdDrawing.id;
      setStoredId(createdDrawing.id);
      const { image, source } = pending.current;
      if (image) {
        drawingsApi
          .saveReferenceImage(createdDrawing.id, image)
          .catch(() => dispatch(toastReducer.actions.showToast({ message: 'drawings.imageFailed', type: 'error' })));
        setPendingImage(null);
      }
      if (source) {
        drawingsApi
          .saveSource(createdDrawing.id, source)
          .catch(() => dispatch(toastReducer.actions.showToast({ message: 'drawings.sourceFailed', type: 'error' })));
        setPendingSource(null);
      }
      onCreated(createdDrawing.id);
      return createdDrawing;
    },
    [dispatch, onCreated],
  );
  const autosave = useDrawingAutosave(idRef.current, drawing.lockKey, build, {
    auto: !fromProject,
    create: isNew ? create : undefined,
  });
  const { changed, save, state: saveState, release } = autosave;

  // From a project nothing is saved until the update, so any way out of the editor (the header,
  // the logo, the browser's back) asks before dropping the edits. Leaving on purpose lifts it.
  const unblock = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!fromProject || saveState !== 'pending') return;
    const lift = history.block((transition) => {
      if (!window.confirm(t('drawings.discardChanges'))) return;
      lift();
      transition.retry();
    });
    unblock.current = lift;
    return () => {
      lift();
      unblock.current = null;
    };
  }, [fromProject, saveState, t]);
  const leaveFreely = useCallback(() => {
    unblock.current?.();
    unblock.current = null;
    release();
  }, [release]);

  const savePreview = useCallback((id = idRef.current) => {
    const plan = live.current;
    if (id === null || !plan || (plan.polygons.length === 0 && !plan.reference)) return;
    lastPreview.current = Date.now();
    drawingsApi.savePreview(id, renderPlanThumbnail(plan.polygons, 640, plan.reference)).catch(() => undefined);
  }, []);

  // A fresh thumbnail now and then while drawing, and when leaving.
  useEffect(() => {
    if (!fromProject && saveState === 'saved' && Date.now() - lastPreview.current > PREVIEW_INTERVAL_MS) savePreview();
  }, [fromProject, saveState, savePreview]);
  useEffect(
    () => () => {
      if (!fromProject) savePreview();
    },
    [fromProject, savePreview],
  );

  useEffect(
    () => () => {
      if (referenceImageUrl?.startsWith('blob:')) URL.revokeObjectURL(referenceImageUrl);
    },
    [referenceImageUrl],
  );

  const handleChange = useCallback(
    (plan: EditedPlan) => {
      const last = live.current;
      live.current = plan;
      if (last && samePlan(last, plan)) return;
      // A new drawing is not stored while it is still empty.
      if (idRef.current === null && !hasContent(plan)) return;
      changed();
    },
    [changed],
  );

  /** Saves what is in the editor (always once, so the server has a document). */
  const saveNow = useCallback(
    () => save(idRef.current === null || (!drawing.document && !autosave.saved())),
    [save, drawing.document, autosave],
  );
  /** The same, resolving to the drawing's id (for a project made from it). */
  const saveForProject = useCallback(async () => ((await saveNow()) ? idRef.current : null), [saveNow]);

  const handleImageImported = useCallback(
    (file: File) => {
      if (fromProject || idRef.current === null) {
        setPendingImage(file);
        setReferenceImageUrl(URL.createObjectURL(file));
        return;
      }
      drawingsApi
        .saveReferenceImage(idRef.current, file)
        .then(setReferenceImageUrl)
        .catch(() => dispatch(toastReducer.actions.showToast({ message: 'drawings.imageFailed', type: 'error' })));
    },
    [dispatch, fromProject],
  );

  // The imported DWG/DXF goes with the drawing, so its lines are there when it is reopened.
  const handleCadImported = useCallback(
    async (dxf: Blob) => {
      try {
        const gzipped = await gzipDxf(dxf);
        if (fromProject || idRef.current === null) setPendingSource(gzipped);
        else await drawingsApi.saveSource(idRef.current, gzipped);
      } catch (error) {
        console.error('Saving the drawing file failed:', error);
        dispatch(toastReducer.actions.showToast({ message: 'drawings.sourceFailed', type: 'error' }));
      }
    },
    [dispatch, fromProject],
  );

  /** Saves the change made for the project to this drawing or a new one; resolves to its id. */
  const saveChange = useCallback(
    async (target: DrawingTarget): Promise<number | null> => {
      let id = idRef.current ?? drawing.id;
      if (target.kind === 'existing') {
        if (!(await save())) return null;
      } else {
        id = (await drawingsApi.create(target.name, build())).id;
      }
      try {
        // A new drawing keeps the plan image too.
        const image =
          pendingImage ??
          (target.kind === 'new' && referenceImageUrl ? await (await fetch(referenceImageUrl)).blob() : null);
        if (image) await drawingsApi.saveReferenceImage(id, image);
      } catch (error) {
        console.error('Saving the plan image failed:', error);
        dispatch(toastReducer.actions.showToast({ message: 'drawings.imageFailed', type: 'error' }));
      }
      try {
        // And the DWG/DXF it is traced from.
        const source =
          pendingSource ??
          (target.kind === 'new' && live.current?.cad && drawing.sourceUrl
            ? await (await fetch(drawing.sourceUrl)).blob()
            : null);
        if (source) await drawingsApi.saveSource(id, source);
      } catch (error) {
        console.error('Saving the drawing file failed:', error);
        dispatch(toastReducer.actions.showToast({ message: 'drawings.sourceFailed', type: 'error' }));
      }
      savePreview(id);
      return id;
    },
    [drawing.id, drawing.sourceUrl, save, build, pendingImage, pendingSource, referenceImageUrl, savePreview, dispatch],
  );

  const rename = (newName: string) => {
    setName(newName);
    nameRef.current = newName;
    if (idRef.current !== null) drawingsApi.rename(idRef.current, newName).catch(() => undefined);
  };

  const saveCheckpoint = async (label: string) => {
    if (!(await saveNow())) {
      dispatch(toastReducer.actions.showToast({ message: 'drawings.saveFailed', type: 'error' }));
      return;
    }
    try {
      const revision = await drawingsApi.checkpoint(idRef.current!, label.trim() || null);
      setRevisions((current) => [...current, revision]);
    } catch {
      dispatch(toastReducer.actions.showToast({ message: 'drawings.checkpointFailed', type: 'error' }));
    }
  };

  const restore = async (revision: number) => {
    try {
      const { document } = await drawingsApi.revision(idRef.current!, revision);
      const restored = fromDrawingDocument(document);
      if (!restored) throw new Error(`Revision ${revision} is not a drawing this editor reads`);
      setOpened((current) => ({ plan: { ...restored, origin }, key: current.key + 1 }));
    } catch (error) {
      console.error('Restoring the checkpoint failed:', error);
      dispatch(toastReducer.actions.showToast({ message: 'drawings.restoreFailed', type: 'error' }));
    }
  };

  const openDialog = (kind: 'create' | 'update') => {
    const plan = live.current;
    if (!plan || plan.polygons.length === 0) {
      dispatch(toastReducer.actions.showToast({ message: 'drawings.emptyDrawing', type: 'warning' }));
      return;
    }
    setDialog({ kind, plan });
  };

  const switchBase = (next: 'project' | 'drawing') => {
    if (saveState === 'pending' && !window.confirm(t('drawings.switchDiscards'))) return;
    setBase(next);
    setOpened((current) => ({ plan: next === 'project' ? projectPlan : drawingPlan, key: current.key + 1 }));
  };

  const leave = () => {
    if (fromProject && saveState === 'pending' && !window.confirm(t('drawings.discardChanges'))) return;
    leaveFreely();
    navigate(projectId ? `/workspace?projectId=${projectId}` : '/drawings');
  };

  const header = (
    <div className="flex flex-col gap-2">
      <DrawingHeader
        name={name}
        onRename={rename}
        saveState={saveState}
        revisions={revisions}
        onCheckpoint={saveCheckpoint}
        onRestore={restore}
        onReload={onReload}
        fromProject={fromProject}
        canCheckpoint={storedId !== null}
      />
      {movedOn && (
        <div className="rounded-lg border border-orange-400/30 bg-orange-400/10 p-3 text-[11px] text-orange-200 flex flex-col gap-1.5">
          <span>{t(base === 'project' ? 'drawings.movedOnProject' : 'drawings.movedOnLatest', { name })}</span>
          <button
            onClick={() => switchBase(base === 'project' ? 'drawing' : 'project')}
            className="self-start underline text-[#B3EC49] hover:text-lime-300"
          >
            {t(base === 'project' ? 'drawings.openLatest' : 'drawings.openProjectVersion')}
          </button>
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="flex-shrink-0 rounded-bl-2xl bg-[#232323] w-full h-[60px] flex justify-between px-6 items-center gap-4 border-t border-gray-800">
      <button
        onClick={leave}
        className="h-9 px-[18px] rounded-full font-medium text-xs border border-[#B3EC49] text-[#B3EC49] hover:border-lime-500"
      >
        {projectId ? t('drawings.backToProject') : t('drawings.backToDrawings')}
      </button>
      <button
        onClick={() => openDialog(projectId ? 'update' : 'create')}
        disabled={!fromProject && saveState === 'conflict'}
        className="h-9 px-[18px] rounded-full font-medium text-xs bg-lime-400 text-black hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {projectId ? t('drawings.updateProject') : t('drawings.createProject')}
      </button>
    </div>
  );

  return (
    <>
      <CadProjectWizard
        key={opened.key}
        edit={{
          drawing: opened.plan,
          referenceImageUrl,
          onChange: handleChange,
          onImageImported: handleImageImported,
          onCadImported: handleCadImported,
          cadSourceUrl: drawing.sourceUrl,
          onCancel: leave,
          startWith,
          header,
          footer,
        }}
      />
      {dialog?.kind === 'create' && (
        <CreateProjectFromDrawingDialog
          defaultName={name}
          plan={dialog.plan}
          referenceImageUrl={referenceImageUrl}
          saveDrawing={saveForProject}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'update' && projectId && (
        <UpdateProjectDialog
          projectId={projectId}
          drawingName={name}
          existingBlocked={saveState === 'conflict'}
          drawingMovedOn={movedOn && base === 'project'}
          plan={dialog.plan}
          referenceImageUrl={referenceImageUrl}
          saveDrawing={saveChange}
          onCancel={() => setDialog(null)}
          // A full load: the workspace downloads the new model and the updated versions.
          onUpdated={() => {
            leaveFreely();
            window.location.assign(`/workspace?projectId=${projectId}`);
          }}
        />
      )}
    </>
  );
}
